<template>
  <div class="patient-schedule">
    <transition name="slideNext">
      <div v-if="loading">
        <loading-messages v-if="loading"></loading-messages>
      </div>
      <div
        v-if="time || schedule.length > 0"
        class="text-center mt-0 mt-md-3 mx-3 ml-md-0"
      >
        <div
          class="row text-primary no-gutters justify-content-center align-items-center"
        >
          <div class="col-auto svg mb-2 mb-md-0">
            <online-consultation-icon
              v-if="service.type === 'telemedicine'"
            ></online-consultation-icon>
            <clinic-icon v-else-if="service.type === 'visit'"></clinic-icon>
            <ambulance-icon
              v-else-if="service.type === 'house_call'"
            ></ambulance-icon>
          </div>
          <div class="col-auto">
            <strong class="ml-2 pt-1 d-block">{{ $t(service.type) }}</strong>
          </div>
        </div>
        <h4 class="pt-0 pt-md-3" v-if="service">
          {{ service.name[authLocale] }}
          <small class="d-block" v-if="doctor">
            {{ doctor.firstname[authLocale] }}
            {{ doctor.lastname[authLocale] }}
          </small>
        </h4>
        <div class="my-2">{{ currentDate | moment("DD MMMM, dddd") }}</div>
        <strong class="mt-1 mb-3 d-block text-primary">
          {{ timeComputed }}
        </strong>
        <div
          v-if="
            schedule.length === 0 || schedule[0].status === 'payment_required'
          "
        >
          <div
            class="input-group mb-3 address"
            v-if="service.type === 'house_call'"
          >
            <div class="input-group-prepend">
              <span class="input-group-text">{{ $t("address") }}:</span>
            </div>
            <input
              type="text"
              class="form-control"
              v-model="address"
              placeholder="რუსთაველის გამზ. 1"
              :disabled="schedule.length > 0"
            />
          </div>
          <a href="#" class="btn btn-primary" @click.prevent="createSchedule">
            {{ Math.round(service.price / 100).toFixed(2) }} ₾
            {{ $t("pay") }}
          </a>
        </div>
        <div v-else-if="schedule.length > 0" class="mb-3">
          <div class="mb-3" v-if="service.type === 'house_call'">
            {{ $t("address") }}:
            <small>{{ schedule[0].address }}</small>
          </div>
          <small class="bg-success text-white svg py-2 px-3 rounded">
            <check-icon></check-icon>
            {{ $t("paid") }}
            {{ Math.round(schedule[0].price / 100).toFixed(2) }} ₾
          </small>
          <div class="mt-2" v-if="schedule[0].interview_id">
            <InterviewScheduleLink :schedule="schedule[0]"></InterviewScheduleLink>
          </div>
        </div>
        <small
          v-if="schedule.length === 0"
          class="my-1 py-2 d-block pointer"
          @click.prevent="cancel"
        >
          {{ $t("cancel") }}
        </small>
      </div>
      <div v-else-if="dates && dates.length > 0">
        <div class="dates mb-3">
          <div class="py-2 px-3 row no-gutters align-items-center">
            <div class="col-auto first">
              <a
                class="pointer"
                @click.prevent="setIndex(0)"
                :class="{ 'opacity-0': index === 0 }"
              >
                <double-arrow-icon></double-arrow-icon>
              </a>
            </div>
            <div class="col-auto prev">
              <a
                class="pointer"
                @click.prevent="setIndex(--index)"
                :class="{ 'opacity-0': index === 0 }"
              >
                <angle-icon></angle-icon>
              </a>
            </div>
            <div class="col text-center">
              <template v-if="dates[index]">
                <strong class="pr-1">
                  {{ dates[index] | moment("DD") }}
                </strong>
                <span>{{ dates[index] | moment("MMM") }}</span>
                <span class="d-block">
                  {{ dates[index] | moment("dddd") }}
                </span>
              </template>
            </div>
            <div class="col-auto next">
              <a
                class="pointer"
                @click.prevent="setIndex(++index)"
                :class="{ 'opacity-0': index === dates.length - 1 }"
              >
                <angle-icon></angle-icon>
              </a>
            </div>
          </div>
        </div>
        <div class="px-3 row no-gutters justify-content-between">
          <div
            class="col-auto text-center border mx-1 mb-2 px-2 text-primary py-1 rounded shadow-sm shadow-hover"
            v-for="(t,index) of times"
            :key="`${service.id}-${t}`"
            @click.prevent="setTime(t,index)"
          >
            <small class="px-2 pointer">{{ t }}</small>
          </div>
        </div>
      </div>
      <div v-else class="col py-3 text-center">
        {{ $t("noSchedule") }}
      </div>
    </transition>
  </div>
</template>
<script>
import { mapGetters, mapMutations } from 'vuex'
import AngleIcon from '@/components/Icons/Angle'
import DoubleArrowIcon from '@/components/Icons/DoubleArrow'
import LoadingMessages from '@/components/Loadings/Message'
import OnlineConsultationIcon from '@/components/Icons/OnlineConsultation'
import ClinicIcon from '@/components/Icons/Clinic'
import AmbulanceIcon from '@/components/Icons/Ambulance'
import CheckIcon from '@/components/Icons/Check'
import InterviewScheduleLink from '@/components/Interviews/ScheduleLink'
export default {
  components: {
    AngleIcon,
    DoubleArrowIcon,
    LoadingMessages,
    OnlineConsultationIcon,
    ClinicIcon,
    AmbulanceIcon,
    CheckIcon,
    InterviewScheduleLink
  },
  props: {
    doctor: {
      type: Object,
      default: null
    },
    service: {
      type: Object,
      required: true
    },
    interviewId: {
      type: String,
      default: null
    },
    dateIndex: {
      type: String,
      default: null
    },
    timeIndex: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      loading: false,
      time: null,
      index: 0,
      address: null
    }
  },
  computed: {
    ...mapGetters({
      isOnline: 'status/isOnline',
      authUser: 'auth/user',
      authLocale: 'auth/locale',
      scheduleByDoctorAndService: 'schedule/byDoctorAndService',
      scheduleByService: 'schedule/byService'
    }),
    currentDate () {
      return this.dates[this.index]
    },
    timeComputed () {
      if (this.schedule.length > 0) {
        return this.$moment(this.schedule[0].start_time).format('HH:mm')
      }
      return this.time
    },
    times () {
      if (!this.service.availability) {
        return []
      }
      const times = this.service.availability[this.currentDate]
      const ret = []
      let found = false
      let busy = []
      if (
        this.doctor &&
        this.doctor.busy &&
        this.doctor.busy[this.currentDate]
      ) {
        busy = this.doctor.busy[this.currentDate]
      }
      for (const t of times) {
        const from = this.$moment(t.from).format('HH:mm')
        const to = this.$moment(t.to).format('HH:mm')
        for (let i = 0; i < 1440; i += this.service.duration) {
          let hour = '' + Math.floor(i / 60)
          let min = '' + (i % 60)
          if (hour.length === 1) {
            hour = '0' + hour
          }
          if (min.length === 1) {
            min = '0' + min
          }
          const time = `${hour}:${min}`
          if (time === from) {
            found = true
          }
          if (time === to) {
            found = false
          }
          if (found) {
            ret.push(time)
          }
        }
      }
      for (const b of busy) {
        const from = this.$moment(b.from).format('HH:mm')
        const to = this.$moment(b.to).format('HH:mm')
        for (let i = 0; i < 1440; i += this.service.duration) {
          let hour = '' + Math.floor(i / 60)
          let min = '' + (i % 60)
          if (hour.length === 1) {
            hour = '0' + hour
          }
          if (min.length === 1) {
            min = '0' + min
          }
          const time = `${hour}:${min}`
          if (time === from) {
            found = true
          }
          if (time === to) {
            found = false
          }
          if (found) {
            for (const i in ret) {
              if (ret[i] === time) {
                ret.splice(i, 1)
              }
            }
          }
        }
      }
      ret.sort((a, b) => {
        if (a > b) {
          return 1
        }
        return -1
      })
      return ret
    },
    schedule () {
      if (this.doctor) {
        return this.scheduleByDoctorAndService(this.doctor.id, this.service.id)
      }
      return this.scheduleByService(this.service.id)
    },
    dates () {
      if (!this.service.availability) {
        return []
      }
      const ret = Object.keys(this.service.availability)
      if (ret) {
        return ret
      }
      return []
    }
  },
  watch: {
    schedule (val) {
      if (val.length > 0) {
        this.address = val[0].address
      }
    }
  },
  mounted () {
    if (this.dateIndex && !isNaN(parseInt(this.dateIndex))) {
      this.index = parseInt(this.dateIndex)
    }
    if (this.index !== null && this.timeIndex && !isNaN(parseInt(this.timeIndex))) {
      const tIndex = parseInt(this.timeIndex)
      if (this.times[tIndex]) {
        this.time = this.times[tIndex]
      }
    }
    if (this.schedule.length > 0) {
      this.address = this.schedule[0].address
    }
  },
  methods: {
    ...mapMutations({
      setSchedule: 'schedule/set',
      setError: 'alert/setError',
      setRedirect: 'auth/setRedirect'
    }),
    setTime (t, i) {
      this.time = t
      this.$router.push({ query: { date: this.index, service: this.service.id, time: i } })
    },
    setIndex (index) {
      if (index < 0 || index > this.dates.length - 1) {
        return
      }
      this.index = index
      this.$router.push({ query: { date: this.index, service: this.service.id } })
    },
    async cancel () {
      this.time = null
    },
    async createSchedule () {
      if (!this.authUser) {
        this.setRedirect(this.$route)
        this.$router.push({ name: 'Login' })
        return
      }
      if (this.schedule.length > 0) {
        this.pay(this.schedule.price)
        return
      }
      try {
        const params = {
          clinic_service_id: this.service.id,
          start_time: this.$moment(`${this.currentDate} ${this.time}`).format(),
          interview_id: this.interviewId
        }
        if (this.doctor) {
          params.assignee_id = this.doctor.id
        }
        if (this.service.type === 'house_call') {
          params.city_id = 'cdd3e657-6a54-4cba-86b4-67695f035a62'
          params.address = this.address
        }
        const response = await this.axios.post(
          '/api/patient/schedules',
          params
        )
        const schedule = response.data.data
        this.setSchedule(schedule)
        if (schedule.assignee && schedule.assignee.firstname) {
          this.$ga.ecommerce.addItem({
            id: schedule.id,
            name: `${schedule.assignee.firstname.en} ${schedule.assignee.lastname.en}`,
            category: 'Doctor',
            price: schedule.price,
            quantity: '1',
            currency: 'GEL'
          })
        } else {
          this.$ga.ecommerce.addItem({
            id: schedule.id,
            name: this.service.name.en,
            category: 'Service',
            price: schedule.price,
            quantity: '1',
            currency: 'GEL'
          })
        }
        this.pay(schedule.price)
      } catch (err) {
        this.setError(err)
      }
    },
    async pay (amount) {
      this.loading = true
      if (
        this.schedule.length > 0 &&
        this.schedule[0].status === 'payment_required'
      ) {
        try {
          const response = await this.axios.get(
            `/api/schedules/${this.schedule[0].id}/payment`
          )
          this.$ga.event({
            eventCategory: 'Schedule',
            eventAction: 'Pay',
            eventLabel: 'GEL',
            eventValue: amount
          })
          const form = document.createElement('form')
          form.method = 'post'
          form.action = 'https://ecommerce.ufc.ge/ecomm2/ClientHandler'
          const hiddenField = document.createElement('input')
          hiddenField.type = 'hidden'
          hiddenField.name = 'trans_id'
          hiddenField.value = response.data.data
          form.appendChild(hiddenField)
          document.body.appendChild(form)
          this.loading = false
          form.submit()
        } catch (err) {
          this.setError(err)
          this.loading = false
        }
      }
    }
  }
}
</script>
<style lang="scss">
.patient-schedule {
  .address {
    .input-group-text {
      background: none;
      border: 0;
      border-bottom: 1px solid #ccc;
    }
    input {
      border: 0;
      border-bottom: 1px solid #ccc;
      font-size: 0.8em;
      padding: 1.15rem 0.75rem;
      &:focus {
        box-shadow: none;
        border-bottom: 1px solid #3c88ff;
      }
    }
  }
  .dates {
    width: 100%;
    font-size: 0.9em;
    background: white;
    // border: 2px solid #3c88ff;
    // box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);
    position: sticky;
    top: 0;
    z-index: 200;
    // color: #fff;
    strong {
      font-size: 1.25em;
    }
    .first {
      transform: rotate(180deg);
      svg {
        width: 36px;
        height: 36px;
      }
    }
    .prev {
      transform: rotate(90deg);
      svg {
        width: 36px;
        height: 36px;
      }
    }
    .next {
      transform: rotate(-90deg);
      svg {
        width: 36px;
        height: 36px;
      }
    }
  }
}
</style>
<i18n>
{
  "ka": {
      "telemedicine": "ონლაინ",
      "visit": "კლინიკაში",
      "house_call": "გამოძახება",
      "noSchedule": "არ არის თავისუფალი ადგილი",
      "pay": "გადახდა",
      "cancel": "გაუქმება",
      "paid": "გადახდილია",
      "interviewInited": "შეიყვანე შენი სიმპტომები",
      "interviewActive": "დაასრულე სიმპტომების შეყვანა",
      "interviewFinished": "სიმპტომები შეყვანილია",
      "interviewCancelled": "სიმპტომები დასრულებულია",
      "address": "მისამართი"
  },
  "en": {
      "telemedicine": "Online",
      "visit": "At Clinic",
      "house_call": "Houce Call",
      "noSchedule": "No Free Slots",
      "pay": "Pay",
      "cancel": "Cancel",
      "paid": "Paid",
      "interviewInited": "Interview Inited",
      "interviewActive": "Interview Active",
      "interviewFinished": "Interview Finished",
      "interviewCancelled": "Interview Cancelled",
      "address": "Address"
  }
}
</i18n>
